import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { useAuthStore } from './store/authStore.js';
import Loader from './components/common/Loader.jsx';
import UpgradeModal from './components/UpgradeModal.jsx';
import LangLayout from './components/layout/LangLayout.jsx';

const Landing = lazy(() => import('./pages/Landing.jsx'));
const Login = lazy(() => import('./pages/Auth/Login.jsx'));
const Register = lazy(() => import('./pages/Auth/Register.jsx'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword.jsx'));
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword.jsx'));
const VerifyEmail = lazy(() => import('./pages/Auth/VerifyEmail.jsx'));

const DashboardLayout = lazy(() => import('./components/layout/DashboardLayout.jsx'));
const DashboardHome = lazy(() => import('./pages/Dashboard/DashboardHome.jsx'));
const InvitationList = lazy(() => import('./pages/Dashboard/InvitationList.jsx'));
const NewInvitation = lazy(() => import('./pages/Dashboard/NewInvitation.jsx'));
const Editor = lazy(() => import('./pages/Editor/Editor.jsx'));
const InvitationGuestbook = lazy(() => import('./pages/Dashboard/InvitationGuestbook.jsx'));
const InvitationGallery = lazy(() => import('./pages/Dashboard/InvitationGallery.jsx'));
const InvitationGift = lazy(() => import('./pages/Dashboard/InvitationGift.jsx'));
const InvitationStory = lazy(() => import('./pages/Dashboard/InvitationStory.jsx'));
const InvitationSettings = lazy(() => import('./pages/Dashboard/InvitationSettings.jsx'));
const InvitationAnalytics = lazy(() => import('./pages/Dashboard/InvitationAnalytics.jsx'));

const Checkout = lazy(() => import('./pages/Checkout/Checkout.jsx'));
const CheckoutSuccess = lazy(() => import('./pages/Checkout/CheckoutSuccess.jsx'));

const PublicInvitation = lazy(() => import('./pages/Invitation/PublicInvitation.jsx'));

const PricingPage = lazy(() => import('./pages/PricingPage.jsx'));
const PrivacyPolicy = lazy(() => import('./pages/Legal/PrivacyPolicy.jsx'));
const TermsOfService = lazy(() => import('./pages/Legal/TermsOfService.jsx'));
const ScannerPage = lazy(() => import('./pages/ScannerPage.jsx'));
const ScanPage = lazy(() => import('./pages/ScanPage.jsx'));
const InvitationGuests = lazy(() => import('./pages/Dashboard/InvitationGuests.jsx'));
const InvitationSessions = lazy(() => import('./pages/Dashboard/InvitationSessions.jsx'));
const InvitationCategories = lazy(() => import('./pages/Dashboard/InvitationCategories.jsx'));
const InvitationGroupLinks = lazy(() => import('./pages/Dashboard/InvitationGroupLinks.jsx'));
const InvitationWaBlast = lazy(() => import('./pages/Dashboard/InvitationWaBlast.jsx'));
const InvitationAttendance = lazy(() => import('./pages/Dashboard/InvitationAttendance.jsx'));
const InvitationRsvpLog = lazy(() => import('./pages/Dashboard/InvitationRsvpLog.jsx'));

const AdminLayout = lazy(() => import('./components/layout/AdminLayout.jsx'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard.jsx'));
const AdminUsers = lazy(() => import('./pages/Admin/AdminUsers.jsx'));
const AdminInvitations = lazy(() => import('./pages/Admin/AdminInvitations.jsx'));
const AdminOrders = lazy(() => import('./pages/Admin/AdminOrders.jsx'));
const AdminTemplates = lazy(() => import('./pages/Admin/AdminTemplates.jsx'));

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, isLoading } = useAuthStore();
  if (isLoading) return <Loader fullscreen />;
  if (!user) return <Navigate to="/en/login" replace />;
  if (adminOnly && user.role !== 'ADMIN') return <Navigate to="/en/dashboard" replace />;
  return children;
}

export default function App() {
  const init = useAuthStore((s) => s.init);

  useEffect(() => {
    init();
  }, [init]);

  return (
    <Suspense fallback={<Loader fullscreen />}>
      <Routes>
        {/* Root → default to English */}
        <Route path="/" element={<Navigate to="/en" replace />} />

        {/* Public scanner — language-agnostic, intended for door staff */}
        <Route path="/scan/:scanToken" element={<ScanPage />} />

        {/* Language-prefixed routes */}
        <Route path="/:lang" element={<LangLayout />}>
          {/* Marketing */}
          <Route index element={<Landing />} />

          {/* Auth */}
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="verify-email" element={<VerifyEmail />} />

          {/* Customer dashboard */}
          <Route
            path="dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardHome />} />
            <Route path="invitations" element={<InvitationList />} />
            <Route path="invitations/new" element={<NewInvitation />} />
            <Route path="invitation/:id/edit" element={<Editor />} />
            <Route path="invitation/:id/guestbook" element={<InvitationGuestbook />} />
            <Route path="invitation/:id/gallery" element={<InvitationGallery />} />
            <Route path="invitation/:id/gift" element={<InvitationGift />} />
            <Route path="invitation/:id/story" element={<InvitationStory />} />
            <Route path="invitation/:id/settings" element={<InvitationSettings />} />
            <Route path="invitation/:id/analytics" element={<InvitationAnalytics />} />
            <Route path="invitation/:id/guests" element={<InvitationGuests />} />
            <Route path="invitation/:id/sessions" element={<InvitationSessions />} />
            <Route path="invitation/:id/categories" element={<InvitationCategories />} />
            <Route path="invitation/:id/group-links" element={<InvitationGroupLinks />} />
            <Route path="invitation/:id/wa-blast" element={<InvitationWaBlast />} />
            <Route path="invitation/:id/attendance" element={<InvitationAttendance />} />
            <Route path="invitation/:id/rsvp-log" element={<InvitationRsvpLog />} />
          </Route>

          <Route
            path="checkout/:invitationId"
            element={
              <ProtectedRoute>
                <Checkout />
              </ProtectedRoute>
            }
          />
          <Route
            path="checkout/success"
            element={
              <ProtectedRoute>
                <CheckoutSuccess />
              </ProtectedRoute>
            }
          />

          {/* Admin */}
          <Route
            path="admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="invitations" element={<AdminInvitations />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="templates" element={<AdminTemplates />} />
          </Route>

          {/* Pricing */}
          <Route path="pricing" element={<PricingPage />} />

          {/* Legal */}
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="terms" element={<TermsOfService />} />

          {/* QR scanner */}
          <Route path="scanner/:slug" element={<ScannerPage />} />
          <Route path="checkin/:slug" element={<ScannerPage />} />

          {/* Public invitation by slug — must be last */}
          <Route path=":slug" element={<PublicInvitation />} />
        </Route>

        {/* Anything else → /en root */}
        <Route path="*" element={<Navigate to="/en" replace />} />
      </Routes>

      {/* Global upgrade modal */}
      <UpgradeModal />
    </Suspense>
  );
}
